import { useState } from 'react';
import { useWishlist } from '../context/WishlistContext';
import { useShopify } from '../context/ShopifyContext';
import type { MappedProduct } from '../hooks/useShopifyProducts';

const WishlistItem = ({ product, onRemove }: { product: MappedProduct; onRemove: () => void }) => {
  const { addToCart } = useShopify();
  const [selectedSize, setSelectedSize] = useState<string>(
    product.variants.find(v => v.availableForSale)?.size ?? product.sizes[0] ?? ''
  );
  const [added, setAdded] = useState(false);
  const [adding, setAdding] = useState(false);

  const hasMultipleSizes = product.sizes.length > 1 && !(product.sizes.length === 1 && product.sizes[0] === 'ONE SIZE');
  const selectedVariant = product.variants.find(v => v.size === selectedSize);
  const canAdd = selectedVariant?.availableForSale ?? false;

  const handleAddToCart = async () => {
    if (!selectedVariant || !canAdd || adding) return;
    setAdding(true);
    try {
      await addToCart(selectedVariant.variantId, 1);
      setAdded(true);
      setTimeout(() => setAdded(false), 1800);
    } catch (err) {
      console.error('Failed to add from wishlist:', err);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="flex gap-4 pb-6 border-b border-[#C9A84C]/12 last:border-b-0 group">
      {/* Image */}
      <div className="w-[88px] h-[88px] flex-shrink-0 bg-white border border-[#0A0A0A]/6 overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <h3
            className="text-[#0A0A0A] text-sm leading-snug tracking-tight line-clamp-2"
            style={{ fontFamily: 'Playfair Display, serif', fontWeight: 400 }}
          >
            {product.name}
          </h3>
          {/* Remove */}
          <button
            onClick={onRemove}
            className="flex-shrink-0 w-7 h-7 flex items-center justify-center text-[#0A0A0A]/25 hover:text-[#C9A84C] transition-colors cursor-pointer -mt-0.5"
            aria-label="Remove from wishlist"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        <p
          className="text-[#C9A84C] text-base"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        >
          ${product.price.toFixed(2)}
        </p>

        {/* Size selector */}
        {hasMultipleSizes && (
          <div className="flex flex-wrap gap-1 pt-0.5">
            {product.variants.map(v => (
              <button
                key={v.variantId}
                onClick={() => setSelectedSize(v.size)}
                disabled={!v.availableForSale}
                className={`px-2.5 py-1 text-[10px] tracking-[1.5px] font-light transition-all cursor-pointer whitespace-nowrap ${
                  !v.availableForSale
                    ? 'border border-[#0A0A0A]/8 text-[#0A0A0A]/20 cursor-not-allowed line-through'
                    : selectedSize === v.size
                    ? 'border border-[#0A0A0A] bg-[#0A0A0A] text-[#F5F5F0]'
                    : 'border border-[#0A0A0A]/20 text-[#0A0A0A]/55 hover:border-[#0A0A0A]/40'
                }`}
              >
                {v.size}
              </button>
            ))}
          </div>
        )}

        {/* Add to cart */}
        <button
          onClick={handleAddToCart}
          disabled={!canAdd || adding}
          className={`mt-1 w-full py-2 text-[10px] tracking-[2px] font-light border transition-all whitespace-nowrap cursor-pointer min-h-[34px] ${
            !canAdd
              ? 'border-[#0A0A0A]/10 text-[#0A0A0A]/25 cursor-not-allowed'
              : added
              ? 'border-[#C9A84C] bg-[#C9A84C] text-[#0A0A0A]'
              : 'border-[#C9A84C] text-[#C9A84C] hover:bg-[#C9A84C] hover:text-[#0A0A0A]'
          }`}
        >
          {!canAdd ? (
            'OUT OF STOCK'
          ) : adding ? (
            'ADDING...'
          ) : added ? (
            <span className="flex items-center justify-center gap-1.5">
              <i className="ri-check-line" />
              ADDED
            </span>
          ) : (
            'ADD TO CART'
          )}
        </button>
      </div>
    </div>
  );
};

export const WishlistDrawer = () => {
  const { wishlist, removeFromWishlist, clearWishlist, isWishlistOpen, closeWishlist } = useWishlist();

  if (!isWishlistOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-[60] transition-opacity"
        onClick={closeWishlist}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full sm:max-w-md bg-[#F5F5F0] z-[60] shadow-2xl flex flex-col animate-slide-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 sm:px-8 sm:py-6 border-b border-[#C9A84C]/20">
          <div className="flex items-center gap-3">
            <h2
              className="text-2xl sm:text-3xl text-[#0A0A0A] tracking-tight"
              style={{ fontFamily: 'Playfair Display, serif', fontWeight: 400 }}
            >
              Wishlist
            </h2>
            {wishlist.length > 0 && (
              <span className="text-[10px] tracking-[2px] text-[#0A0A0A]/35 font-light mt-1">
                {wishlist.length} {wishlist.length === 1 ? 'ITEM' : 'ITEMS'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {wishlist.length > 0 && (
              <button
                onClick={clearWishlist}
                className="text-[10px] tracking-[1.5px] text-[#0A0A0A]/30 hover:text-[#0A0A0A]/60 transition-colors cursor-pointer font-light underline underline-offset-2 whitespace-nowrap"
              >
                CLEAR ALL
              </button>
            )}
            <button
              onClick={closeWishlist}
              className="w-11 h-11 flex items-center justify-center text-[#0A0A0A]/40 hover:text-[#0A0A0A] transition-colors cursor-pointer"
              aria-label="Close wishlist"
            >
              <i className="ri-close-line text-2xl sm:text-3xl" />
            </button>
          </div>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-6 sm:px-8">
          {wishlist.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 flex items-center justify-center mb-5">
                <i className="ri-heart-3-line text-5xl text-[#0A0A0A]/10" />
              </div>
              <p
                className="text-[#0A0A0A]/50 text-base mb-2"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Your wishlist is empty
              </p>
              <p className="text-[#0A0A0A]/30 text-xs tracking-[2px] font-light mb-8">
                SAVE ITEMS YOU LOVE
              </p>
              <button
                onClick={closeWishlist}
                className="px-8 py-4 border border-[#C9A84C] text-[#C9A84C] text-xs tracking-[3px] font-light hover:bg-[#C9A84C] hover:text-[#0A0A0A] transition-all whitespace-nowrap cursor-pointer min-h-[44px]"
              >
                BROWSE COLLECTION
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {wishlist.map(product => (
                <WishlistItem
                  key={product.id}
                  product={product}
                  onRemove={() => removeFromWishlist(product.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer CTA */}
        {wishlist.length > 0 && (
          <div className="border-t border-[#C9A84C]/20 px-6 py-5 sm:px-8">
            <p className="text-[#0A0A0A]/30 text-xs text-center tracking-[1px] font-light">
              Items are saved to this browser only
            </p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slide-in {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in { animation: slide-in 0.4s ease-out; }
      `}</style>
    </>
  );
};
